
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { 
  BookOpen, 
  Users, 
  Calendar,
  DollarSign, 
  User, 
  Menu,
  Home,
  LogOut,
  ClipboardList
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";

interface TeacherLayoutProps {
  children: React.ReactNode;
}

const TeacherLayout = ({ children }: TeacherLayoutProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const navigationItems = [
    { name: "Dashboard", icon: Home, path: "/teacher/dashboard", key: "dashboard" },
    { name: "My Classes", icon: BookOpen, path: "/teacher/classes", key: "classes" },
    { name: "Attendance", icon: ClipboardList, path: "/teacher/attendance", key: "attendance" },
    { name: "Students", icon: Users, path: "/teacher/students", key: "students" },
    { name: "Fees", icon: DollarSign, path: "/teacher/fees", key: "fees" },
    { name: "Profile", icon: User, path: "/teacher/profile", key: "profile" },
  ];

  const handleLogout = () => {
    localStorage.removeItem('teacherData');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('dashboardStats');
    toast({
      title: "Logged out successfully",
      description: "See you soon!"
    });
    navigate('/');
  };

  const NavigationContent = () => (
    <div className="flex flex-col h-full">
      <div className="p-4 sm:p-6 border-b">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-indigo-100 rounded-full flex items-center justify-center">
            <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="font-semibold text-gray-800 text-sm sm:text-base truncate">TattvaSphere</h2>
            <p className="text-xs sm:text-sm text-gray-600 truncate">Teacher Portal</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 p-2 sm:p-4 overflow-y-auto">
        <ul className="space-y-1 sm:space-y-2">
          {navigationItems.map((item) => (
            <li key={item.key}>
              <Button
                variant={location.pathname === item.path ? "default" : "ghost"}
                className={`w-full justify-start text-xs sm:text-sm ${
                  location.pathname === item.path 
                    ? "bg-indigo-600 text-white" 
                    : "text-gray-700 hover:bg-gray-100"
                }`}
                onClick={() => {
                  navigate(item.path);
                  setIsOpen(false);
                }}
              >
                <item.icon className="w-4 h-4 mr-2 sm:mr-3 flex-shrink-0" />
                <span className="truncate">{item.name}</span>
              </Button>
            </li>
          ))}
        </ul>
      </nav>

      <div className="p-2 sm:p-4 border-t">
        <Button 
          variant="outline" 
          className="w-full justify-start text-red-600 border-red-200 hover:bg-red-50 text-xs sm:text-sm"
          onClick={handleLogout}
        >
          <LogOut className="w-4 h-4 mr-2 sm:mr-3 flex-shrink-0" />
          <span className="truncate">Logout</span>
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="lg:hidden bg-white shadow-sm p-3 sm:p-4 flex items-center justify-between">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
          <div className="w-6 h-6 sm:w-8 sm:h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
            <BookOpen className="w-3 h-3 sm:w-4 sm:h-4 text-indigo-600" />
          </div>
          <h1 className="font-semibold text-gray-800 text-sm sm:text-base truncate">TattvaSphere</h1>
        </div>
        
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" size="icon" className="h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0">
              <Menu className="w-4 h-4" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-64 sm:w-72">
            <NavigationContent />
          </SheetContent>
        </Sheet>
      </div>

      <div className="lg:flex">
        <div className="hidden lg:block w-64 xl:w-72 bg-white shadow-sm h-screen fixed">
          <NavigationContent />
        </div>

        <main className="lg:ml-64 xl:ml-72 flex-1 min-w-0">
          <div className="p-3 sm:p-4 lg:p-6 max-w-full">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default TeacherLayout;
